import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useGenerateBvh } from '@r3f/staticGeometryWorkerController/worker-hooks';
import { Center } from '@react-three/drei';
import { memo, useMemo, useRef, useEffect } from 'react';
import { Box3, BufferGeometry, Mesh, Vector3 } from 'three';
import { useApplyRenderOrderToModel } from '../../hooks/useApplyRenderOrderToModel';
import { useCloneGLTFMaterials } from '../../hooks/useCloneGLTFMaterials';
import usePlayModelAnimations from '../../hooks/usePlayModelAnimations';
import CustomBillboard from '../CustomBillboard/CustomBillboard';
import { useGLTFArrayBufferLoader } from './hooks';
// import {  MeshBVHHelper } from 'three-mesh-bvh';
const Model3d = ({ model3dUrl = '', scale: s, position: p, rotation: r, animationName = '', isPlayingAnimation = false, animationRepetitions: animationRep, animationRunCounter: runCounter, onPointerUp, onPointerDown, onPointerMove, onDoubleClick, onAnimationEnd, onPointerOut, onPointerOver, renderOrder = 0, centerModelToBBox = true, originalBBox, id = '', raycast, idleAnimation, idlePoseTime, isClampWhenFinished, castShadow, receiveShadow, animationTransformGroupPrefix = '', enableBVH = true, sceneId = '', bvhWorkerController: bvhwc, staticGeometryWorkerController: sgwc, billboard, isCameraOrbiting }) => {
    const animationRepetitions = animationRep ?? Infinity;
    const scaleGroupRef = useRef(null);
    const hitGroupRef = useRef(null);
    const scale = useMemo(() => s, [s]);
    const position = useMemo(() => p, [p]);
    const rotation = useMemo(() => r, [r]);
    const userData = useMemo(() => ({ renderOrder, contentId: id }), [id, renderOrder]);
    const hitMesh = useRef(new Mesh(new BufferGeometry()));
    const hasRunIdlePoseOnInitialisedCb = useRef(false);
    const hasRunSetAnimationName = useRef(false);
    // const meshBVHHelperRef = useRef(new MeshBVHHelper(hitMesh.current));
    // custom loader that converts skinned meshes into meshes (and adds a render order to meshes with transparent materials)
    const { scene: modelScene, isSkinned, animations, arrayBuffer } = useGLTFArrayBufferLoader(model3dUrl, { castShadow, receiveShadow });
    // clone materials in model so that changes to materials don't affect other models from same cache
    const sceneWithClonedMaterial = useCloneGLTFMaterials(modelScene);
    // listen to bvh updates and update hitmesh with new static geometry and bvh
    useGenerateBvh({ id, sgwc, bvhwc }, (bvh, geo) => {
        if (!enableBVH)
            return;
        hitMesh.current.geometry = geo;
        hitMesh.current.geometry.computeBoundingBox();
        hitMesh.current.geometry.computeBoundingSphere();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        hitMesh.current.geometry.boundsTree = bvh;
        // meshBVHHelperRef.current.update();
    });
    const sceneBBoxScale = useMemo(() => {
        const bboxScale = originalBBox ? new Vector3(...originalBBox) : new Box3().setFromObject(sceneWithClonedMaterial).getSize(new Vector3());
        return new Vector3(1, 1, 1).divide(bboxScale);
    }, [originalBBox, sceneWithClonedMaterial]);
    useEffect(() => {
        if (!enableBVH)
            return;
        if (!isPlayingAnimation) {
            sgwc?.reset(id, model3dUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlayingAnimation]);
    useEffect(() => {
        if (!enableBVH)
            return;
        // Calculate and add bvh to static geometries in place (no hitmesh required)
        if (!isSkinned) {
            const geometries = [];
            sceneWithClonedMaterial.traverse((node) => {
                if (node.isMesh) {
                    geometries.push(node.geometry);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    node.geometry.computeBoundsTree?.();
                    // const helper = new MeshBVHHelper(node as Mesh)
                    // node.parent?.add(helper)
                }
            });
            return;
        }
        // if (typeof idleAnimation !== 'undefined' && idlePoseTime) sgwc?.generateStaticGeometry(model3dUrl, id);
        if (!arrayBuffer || !hitGroupRef.current)
            return;
        // Send model array buffer to worker to generate second gltf model there
        if (raycast)
            hitMesh.current.raycast = raycast;
        hitGroupRef.current?.add(hitMesh.current);
        // DEBUGGING //
        // hitMesh.current.material = new MeshNormalMaterial();
        // hitMesh.current.geometry.computeVertexNormals();
        // meshBVHHelperRef.current.update();
        // hitGroupRef.current?.add(meshBVHHelperRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [arrayBuffer, isSkinned]);
    // Add render order to meshes with transparent materials
    useApplyRenderOrderToModel(sceneWithClonedMaterial, renderOrder);
    const onAnimationTimeChange = (delta) => {
        if (!enableBVH)
            return;
        if (!sgwc?.getActiveAnimationName(id) || !sgwc?.isModelInitialised(model3dUrl)) {
            sgwc?.updateAnimationTimeByMissedTimeStep(delta, id);
            return;
        }
        sgwc?.updateWorkerAnimationByTimeStep(delta, id, model3dUrl, true);
    };
    const onIdlePose = (name, time) => {
        if (!enableBVH)
            return;
        if (!sgwc?.isModelInitialised(model3dUrl)) {
            if (!hasRunIdlePoseOnInitialisedCb.current) {
                sgwc?.onInitialised(() => onIdlePose(name, time), model3dUrl);
                hasRunIdlePoseOnInitialisedCb.current = true;
            }
            return;
        }
        sgwc?.setAnimationName(name, id);
        sgwc?.setWorkerAnimationTime(time, id, model3dUrl);
    };
    const onAnimationNameChange = (name) => {
        if (!enableBVH)
            return;
        // if (!sgwc?.isModelInitialised(model3dUrl)) return;
        if (!sgwc?.isModelInitialised(model3dUrl)) {
            if (!hasRunSetAnimationName.current) {
                sgwc?.onInitialised(() => onAnimationNameChange(name), model3dUrl);
                hasRunSetAnimationName.current = true;
            }
            return;
        }
        sgwc?.setAnimationName(name, id);
        if (name === idleAnimation) {
            sgwc?.setAnimationData({ loop: true, name, clampWhenFinished: false, url: model3dUrl, id });
        }
        if (name !== idleAnimation) {
            sgwc?.setAnimationData({
                repetitions: animationRep,
                clampWhenFinished: !!isClampWhenFinished,
                loop: typeof animationRep === 'undefined',
                name,
                url: model3dUrl,
                id,
            });
        }
    };
    usePlayModelAnimations({
        isPlayingAnimation,
        animationName,
        animations,
        repetitions: animationRepetitions,
        modelScene: sceneWithClonedMaterial,
        runCounter,
        onAnimationEnd,
        idleAnimation,
        idlePoseTime,
        isClampWhenFinished,
        onAnimationNameChange,
        onAnimationTimeChange,
        onIdlePose,
    });
    return (_jsx(CustomBillboard, { raycast: raycast, name: `${animationTransformGroupPrefix}${id}`, userData: userData, position: position, rotation: rotation, follow: billboard?.[0], lockY: !billboard?.[1], isCameraOrbiting: isCameraOrbiting, children: _jsx("group", { name: `${animationTransformGroupPrefix}inner_${id}`, children: _jsx("group", { ref: scaleGroupRef, scale: scale, children: _jsxs(Center, { disable: !centerModelToBBox, scale: sceneBBoxScale, children: [enableBVH && isSkinned && (_jsx("group", { name: isSkinned ? `hit-test-group-wrapper-${id}` : '', children: _jsx("group", { name: isSkinned ? `sceneId_${sceneId}` : '', visible: false, userData: { type: 'skinnedHitGroup' }, onPointerDown: isSkinned ? onPointerDown || undefined : undefined, onPointerUp: isSkinned ? onPointerUp || undefined : undefined, onPointerMove: isSkinned ? onPointerMove || undefined : undefined, onDoubleClick: isSkinned ? onDoubleClick || undefined : undefined, onPointerOut: isSkinned ? onPointerOut || undefined : undefined, onPointerOver: isSkinned ? onPointerOver || undefined : undefined, children: _jsx("group", { ref: hitGroupRef, userData: userData }) }) })), _jsx("group", { name: !isSkinned ? `hit-test-group-wrapper-${id}` : '', children: _jsx("group", { name: !isSkinned ? `sceneId_${sceneId}` : '', onPointerDown: !isSkinned || !enableBVH ? onPointerDown || undefined : undefined, onPointerUp: !isSkinned || !enableBVH ? onPointerUp || undefined : undefined, onPointerMove: !isSkinned || !enableBVH ? onPointerMove || undefined : undefined, onDoubleClick: !isSkinned || !enableBVH ? onDoubleClick || undefined : undefined, onPointerOut: !isSkinned || !enableBVH ? onPointerOut || undefined : undefined, onPointerOver: !isSkinned || !enableBVH ? onPointerOver || undefined : undefined, children: _jsx("primitive", { name: `${id}-primitive`, raycast: raycast, object: sceneWithClonedMaterial }, sceneWithClonedMaterial.uuid) }) })] }) }) }) }, `custom_billboard_group-${sceneWithClonedMaterial.uuid}`));
};
Model3d.displayName = 'Model3d';
export default memo(Model3d);
